import type { PeriodInput, ReportKpi, ReportValue, SteeringRow, Week } from 'chery-contracts';
import { BLANK_DISPLAY, formatCompletion, formatValue, isNumericUnit, MOVEMENT_GLYPHS, PILLAR_META } from 'chery-kpi-engine';
import { csvDocument, csvNumber, csvText, UNIT_LABELS, VALUE_TYPE_LABELS } from '../../lib/csv';
import { formatDayMonth } from '../system/system-display';
import { summaryTexts, weekDateLabel, type SummaryTexts } from '../tracker/tracker-display';

/**
 * Presentation rules of the steering report (plan §7) — pure, so the period choices, the cell texts, the chart
 * geometry and the CSV are unit-tested. The numbers themselves arrive resolved from `reports.steering`; nothing
 * here recomputes a Total, an Average or a completion. Display rounding is one decimal everywhere.
 */

type UnitOf = Pick<ReportKpi, 'unit' | 'currency'>;

// ---------------------------------------------------------------------------------------------------------------
// Period

export const PERIOD_CHOICES = ['QUARTER', 'LAST_13', 'LAST_26', 'ALL', 'CUSTOM'] as const;
export type PeriodChoice = (typeof PERIOD_CHOICES)[number];

/** The report opens on the current quarter (Alcaraz's default since its Phase 6A). */
export const DEFAULT_PERIOD_CHOICE: PeriodChoice = 'QUARTER';

export interface CustomRange {
  fromWeekId: string;
  toWeekId: string;
}

/** The `period` sent to the API; `null` for Custom until a valid From/To exists. */
export const periodFor = (choice: PeriodChoice, custom: CustomRange | null): PeriodInput | null => {
  switch (choice) {
    case 'QUARTER':
      return { kind: 'QUARTER' };
    case 'LAST_13':
      return { kind: 'LAST_N', n: 13 };
    case 'LAST_26':
      return { kind: 'LAST_N', n: 26 };
    case 'ALL':
      return { kind: 'ALL' };
    case 'CUSTOM':
      return custom ? { kind: 'CUSTOM', fromWeekId: custom.fromWeekId, toWeekId: custom.toWeekId } : null;
  }
};

/** `4 Oct 2026 → 27 Dec 2026` — the range a report covers. */
export const rangeLabel = (weeks: readonly Pick<Week, 'weekDate'>[]): string => {
  const first = weeks[0];
  const last = weeks[weeks.length - 1];

  return first && last ? `${weekDateLabel(first.weekDate)} → ${weekDateLabel(last.weekDate)}` : BLANK_DISPLAY;
};

/** `4 Oct` over `W40`: a week column header. */
export const weekShortLabel = (week: Pick<Week, 'weekDate'>): string => formatDayMonth(week.weekDate);

// ---------------------------------------------------------------------------------------------------------------
// Cells

/** `1,450.0`, `12.5%`, `JOD 1,450.0`, a Text KPI's text, or `—` for a blank. */
export const reportValueText = (kpi: UnitOf, value: ReportValue | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return BLANK_DISPLAY;
  }

  if (typeof value === 'string') {
    return value;
  }

  return isNumericUnit(kpi.unit) ? formatValue(kpi.unit, value, kpi.currency) : String(value);
};

const signed = (text: string, value: number): string => (value > 0 ? `+${text}` : text);

export interface MovementTexts {
  glyph: string;
  /** `+3.0`, `JOD -120.0`. */
  change: string;
  /** `+7.3%`, or empty when the previous value was 0. */
  pct: string;
}

/** The neutral movement of the latest week against the week before it; `null` when there is nothing to compare. */
export const movementTexts = (kpi: UnitOf, row: Pick<SteeringRow, 'movement' | 'change' | 'changePct'>): MovementTexts | null => {
  if (row.movement === null || row.change === null || !isNumericUnit(kpi.unit)) {
    return null;
  }

  return {
    glyph: MOVEMENT_GLYPHS[row.movement],
    change: signed(formatValue(kpi.unit, row.change, kpi.currency), row.change),
    pct: row.changePct === null ? '' : signed(`${formatValue('DECIMAL', row.changePct)}%`, row.changePct),
  };
};

/** Total / Average / Latest with the very formatter the Tracker's summary columns use. */
export const rangeTexts = (kpi: UnitOf, row: Pick<SteeringRow, 'total' | 'average' | 'rangeLatest'>): SummaryTexts =>
  summaryTexts(kpi, { total: row.total, average: row.average, latest: row.rangeLatest });

const DIRECTION_TEXT = { AT_LEAST: 'at least', AT_MOST: 'at most' } as const;
const BASIS_TEXT = { TOTAL: 'range Total', AVERAGE: 'range Average', LATEST: 'Latest value' } as const;

export interface ReportTargetTexts {
  value: string;
  direction: string;
  completion: string;
  title: string;
}

/** The target column: its value, direction, completion and a tooltip naming the figure it is compared with. */
export const targetTexts = (kpi: UnitOf, row: Pick<SteeringRow, 'target' | 'completion' | 'completionBasis'>): ReportTargetTexts | null => {
  if (!row.target || !row.completionBasis || !isNumericUnit(kpi.unit)) {
    return null;
  }

  const value = formatValue(kpi.unit, row.target.value, kpi.currency);
  const direction = DIRECTION_TEXT[row.target.direction];
  const completion = formatCompletion(row.completion);

  return {
    value,
    direction,
    completion,
    title:
      `${row.target.name}: ${direction} ${value} · ${weekDateLabel(row.target.fromWeekDate)} → ${weekDateLabel(row.target.toWeekDate)}\n` +
      `Completion ${completion}, comparing the ${BASIS_TEXT[row.completionBasis]} with the target.`,
  };
};

/** `Number`, `Currency (JOD)` — the title of one unit/currency chart. */
export const groupTitle = (group: { unit: ReportKpi['unit']; currency: ReportKpi['currency'] }): string =>
  group.currency ? `${UNIT_LABELS[group.unit]} (${group.currency})` : UNIT_LABELS[group.unit];

// ---------------------------------------------------------------------------------------------------------------
// Chart geometry (inline SVG, no chart library)

/** The fixed categorical order of `--series-1…8`; a ninth series is never a generated hue — it folds. */
export const SERIES_SLOTS = 8;

export interface ChartScale {
  min: number;
  max: number;
  ticks: number[];
}

/** 1, 2, 2.5 or 5 × 10ⁿ — the smallest of them at least `rough`. */
export const niceStep = (rough: number): number => {
  const power = 10 ** Math.floor(Math.log10(rough));
  const fraction = rough / power;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;

  return nice * power;
};

/**
 * A value axis over every drawn number (and a target line): always including zero, so a small change is never made
 * to look large, with about four clean ticks. Blanks are not numbers and do not stretch the axis.
 */
export const chartScale = (values: readonly (number | null)[], extra: readonly number[] = []): ChartScale => {
  const numbers = [...values.filter((value): value is number => value !== null && Number.isFinite(value)), ...extra];
  const low = Math.min(0, ...numbers);
  let high = Math.max(0, ...numbers);

  if (high === low) {
    high = low + 1;
  }

  const step = niceStep((high - low) / 4);
  const min = Math.floor(low / step) * step;
  const max = Math.ceil(high / step) * step;
  const ticks: number[] = [];

  for (let tick = min; tick <= max + step / 2; tick += step) {
    ticks.push(Number(tick.toPrecision(12)));
  }

  return { min, max, ticks };
};

/** Axis text: `0`, `950`, `1.5K`, `12K`, `2.4M`, `0.5`. */
export const compactTick = (value: number): string => {
  const size = Math.abs(value);
  const trim = (text: string): string => text.replace(/\.0$/, '');

  if (size >= 1_000_000) {
    return `${trim((value / 1_000_000).toFixed(1))}M`;
  }

  if (size >= 1_000) {
    return `${trim((value / 1_000).toFixed(1))}K`;
  }

  return trim(value.toFixed(Number.isInteger(value) ? 0 : 1));
};

/**
 * The SVG path of a series: one sub-path per run of consecutive values, so a blank week is a gap in the line —
 * never a drop to zero. A lone value between two blanks has no line; `isolatedPoints` marks it with a dot.
 */
export const linePath = (points: readonly (number | null)[], x: (index: number) => number, y: (value: number) => number): string => {
  const parts: string[] = [];
  let open = false;

  points.forEach((value, index) => {
    if (value === null) {
      open = false;

      return;
    }

    parts.push(`${open ? 'L' : 'M'}${x(index).toFixed(1)},${y(value).toFixed(1)}`);
    open = true;
  });

  return parts.join(' ');
};

/** Indexes of values with a blank (or nothing) on both sides. */
export const isolatedPoints = (points: readonly (number | null)[]): number[] =>
  points.flatMap((value, index) => (value !== null && (points[index - 1] ?? null) === null && (points[index + 1] ?? null) === null ? [index] : []));

/** Index of the last non-blank point, for the end marker; -1 when the series is blank. */
export const lastPointIndex = (points: readonly (number | null)[]): number => {
  for (let index = points.length - 1; index >= 0; index -= 1) {
    if (points[index] !== null) {
      return index;
    }
  }

  return -1;
};

/** Show every `n`-th week label so labels keep at least `minGap` px between them. */
export const labelEvery = (count: number, plotWidth: number, minGap = 56): number => Math.max(1, Math.ceil((count * minGap) / plotWidth));

/**
 * A target line belongs on a weekly chart only when it is compared with a weekly-sized figure (an Average or a
 * Latest value). A target compared with the range Total (an annual 800 vehicles) would dwarf weekly points of 40.
 */
export const showsTargetLine = (row: Pick<SteeringRow, 'target' | 'completionBasis'>): boolean =>
  row.target !== null && (row.completionBasis === 'AVERAGE' || row.completionBasis === 'LATEST');

// ---------------------------------------------------------------------------------------------------------------
// CSV (wide: one row per KPI)

export const REPORT_CSV_FIXED_HEADERS = ['Pillar', 'KPI', 'Unit', 'Currency', 'Value Type'] as const;
export const REPORT_CSV_SUMMARY_HEADERS = ['Total', 'Average', 'Latest', 'Target'] as const;

export interface ReportCsvInput {
  kpis: readonly ReportKpi[];
  weeks: readonly Pick<Week, 'id' | 'weekDate'>[];
  values: Readonly<Record<string, ReportValue>>;
  rows: readonly SteeringRow[];
}

const valueCell = (value: ReportValue | undefined): string => {
  if (value === undefined) {
    return '';
  }

  return typeof value === 'string' ? csvText(value) : csvNumber(value);
};

/**
 * Pillar, KPI, Unit, Currency, Value Type, one column per week (its reporting Sunday), Total, Average, Latest,
 * Target — exactly the data table and the summary on screen, at display precision; blanks are empty cells.
 */
export const buildReportCsv = ({ kpis, weeks, values, rows }: ReportCsvInput): string => {
  const rowOf = new Map(rows.map((row) => [row.kpiId, row]));
  const header = [...REPORT_CSV_FIXED_HEADERS, ...weeks.map((week) => week.weekDate), ...REPORT_CSV_SUMMARY_HEADERS];
  const lines = kpis.map((kpi) => {
    const row = rowOf.get(kpi.id);

    return [
      csvText(PILLAR_META[kpi.pillar].label),
      csvText(kpi.name),
      UNIT_LABELS[kpi.unit],
      kpi.currency ?? '',
      VALUE_TYPE_LABELS[kpi.valueType],
      ...weeks.map((week) => valueCell(values[`${kpi.id}:${week.id}`])),
      csvNumber(row?.total ?? null),
      csvNumber(row?.average ?? null),
      csvNumber(row?.rangeLatest ?? null),
      csvNumber(row?.target?.value ?? null),
    ];
  });

  return csvDocument([header, ...lines]);
};

/** `chery-steering-report-2026-10-04_to_2026-12-27.csv`. */
export const reportCsvFileName = (weeks: readonly Pick<Week, 'weekDate'>[]): string =>
  `chery-steering-report-${weeks[0]?.weekDate ?? 'empty'}_to_${weeks[weeks.length - 1]?.weekDate ?? 'empty'}.csv`;
